import React, { useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { useTranslation } from "react-i18next";
import { messages } from "../../locales/messages";
import { GfImage } from "@swagger/typescript-fetch-goflyer";

export interface ImagePreviewZoomModalProps {
  open: boolean;
  onClose: () => void;
  imageSrc?: string;
  imageAlt?: string;
  onDownload?: () => void;
  downloadDisabled?: boolean;
  title?: string;
  images?: GfImage[];
  currentIndex?: number;
  onNavigate?: (index: number) => void;
}

const ImagePreviewZoomModal: React.FC<ImagePreviewZoomModalProps> = ({
  open,
  onClose,
  imageSrc,
  imageAlt = "Image Preview",
  onDownload,
  downloadDisabled = false,
  title,
  images = [],
  currentIndex = 0,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const lastTapRef = useRef<number>(0);
  const DOUBLE_TAP_STEP = 1.5;
  const SCALE_THRESHOLD = 1.1;
  const ANIMATION_TIME = 200;
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Use images array if provided, otherwise fall back to single imageSrc
  const hasMultipleImages = images && images.length > 0;
  const currentImage = hasMultipleImages ? images[currentIndex] : null;
  const displaySrc = currentImage?.src || imageSrc;
  const displayAlt = currentImage?.alt || imageAlt;

  const clampPositionToBounds = (
    context: ReactZoomPanPinchRef,
    targetScale: number,
    desiredX: number,
    desiredY: number,
  ) => {
    const instance = context.instance;
    const wrapper = instance.wrapperComponent;
    const content = instance.contentComponent;
    if (!wrapper || !content) {
      return { x: desiredX, y: desiredY };
    }

    const wrapperWidth = wrapper.clientWidth || wrapper.offsetWidth;
    const wrapperHeight = wrapper.clientHeight || wrapper.offsetHeight;
    const contentWidth = content.clientWidth || content.offsetWidth;
    const contentHeight = content.clientHeight || content.offsetHeight;

    if (!wrapperWidth || !wrapperHeight || !contentWidth || !contentHeight) {
      return { x: desiredX, y: desiredY };
    }

    const newContentWidth = contentWidth * targetScale;
    const newContentHeight = contentHeight * targetScale;
    const diffWidth = wrapperWidth - newContentWidth;
    const diffHeight = wrapperHeight - newContentHeight;
    const { centerZoomedOut, limitToBounds } = instance.setup;
    const widthFactor =
      wrapperWidth > newContentWidth
        ? diffWidth * (centerZoomedOut ? 1 : 0.5)
        : 0;
    const heightFactor =
      wrapperHeight > newContentHeight
        ? diffHeight * (centerZoomedOut ? 1 : 0.5)
        : 0;

    const minX = wrapperWidth - newContentWidth - widthFactor;
    const maxX = widthFactor;
    const minY = wrapperHeight - newContentHeight - heightFactor;
    const maxY = heightFactor;

    const clamp = (value: number, min: number, max: number) => {
      if (limitToBounds === false) {
        return value;
      }
      if (value < min) {
        return min;
      }
      if (value > max) {
        return max;
      }
      return value;
    };

    instance.bounds = {
      minPositionX: minX,
      maxPositionX: maxX,
      minPositionY: minY,
      maxPositionY: maxY,
    };

    return {
      x: clamp(desiredX, minX, maxX),
      y: clamp(desiredY, minY, maxY),
    };
  };

  const toggleZoomAtPoint = (clientX: number, clientY: number) => {
    const context = transformRef.current;
    const instance = context?.instance;
    const state = context?.state ?? instance?.transformState;
    if (!context || !instance || !state) {
      return;
    }
    const wrapper = instance.wrapperComponent;
    if (!wrapper) {
      return;
    }

    const { scale, positionX, positionY } = state;
    const { maxScale = 6, minScale = 1 } = instance.props;
    const shouldZoomIn = scale <= SCALE_THRESHOLD;
    const targetScale = shouldZoomIn
      ? Math.min(scale + DOUBLE_TAP_STEP, maxScale)
      : Math.max(1, minScale);

    const rect = wrapper.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;

    const contentX = (relativeX - positionX) / scale;
    const contentY = (relativeY - positionY) / scale;

    const nextPositionX = relativeX - contentX * targetScale;
    const nextPositionY = relativeY - contentY * targetScale;

    const { x, y } = clampPositionToBounds(
      context,
      targetScale,
      nextPositionX,
      nextPositionY,
    );

    context.setTransform(x, y, targetScale, ANIMATION_TIME);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    }
  };

  const handleOpenInNewTab = () => {
    const urlToOpen = displaySrc;
    if (urlToOpen) {
      window.open(urlToOpen, "_blank");
    }
  };

  const handlePrevious = useCallback(() => {
    if (hasMultipleImages && currentIndex > 0 && onNavigate) {
      onNavigate(currentIndex - 1);
    }
  }, [hasMultipleImages, currentIndex, onNavigate]);

  const handleNext = useCallback(() => {
    if (hasMultipleImages && currentIndex < images.length - 1 && onNavigate) {
      onNavigate(currentIndex + 1);
    }
  }, [hasMultipleImages, currentIndex, images.length, onNavigate]);

  const handleThumbnailClick = useCallback(
    (index: number) => {
      if (onNavigate && index !== currentIndex) {
        onNavigate(index);
      }
    },
    [onNavigate, currentIndex],
  );

  // Keyboard navigation
  useEffect(() => {
    if (!open || !hasMultipleImages) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, hasMultipleImages, handlePrevious, handleNext]);

  // Scroll active thumbnail into view
  useEffect(() => {
    if (thumbnailRefs.current[currentIndex]) {
      thumbnailRefs.current[currentIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentIndex]);

  // Reset transform when image changes
  useEffect(() => {
    if (transformRef.current && displaySrc) {
      transformRef.current.resetTransform();
      transformRef.current.centerView(1, 0);
    }
  }, [displaySrc]);

  const handleZoomIn = () => {
    transformRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    transformRef.current?.zoomOut();
  };

  const handleReset = () => {
    transformRef.current?.resetTransform();
    transformRef.current?.centerView(1, 0);
  };

  const handleFitScreen = () => {
    transformRef.current?.resetTransform();
    transformRef.current?.centerView(1, 0);
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    toggleZoomAtPoint(event.clientX, event.clientY);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLImageElement>) => {
    if (e.changedTouches.length !== 1) {
      lastTapRef.current = 0;
      return;
    }

    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTapRef.current;

    // Double tap detection (within 300ms)
    if (tapLength < 300 && tapLength > 0) {
      e.preventDefault();
      const touch = e.changedTouches[0];
      if (touch) {
        toggleZoomAtPoint(touch.clientX, touch.clientY);
      }
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = currentTime;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          background: "rgba(0,0,0,0.95)",
          backdropFilter: "blur(10px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#fff",
          pr: { xs: 2, sm: 3 },
          pl: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h6"
          component="span"
          sx={{ fontSize: { xs: "0.9rem", sm: "1.25rem" }, flex: 1 }}
        >
          {title || t(messages.imagePreviewTitle())}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 0.5, sm: 1 },
            alignItems: "center",
          }}
        >
          <Tooltip title={t(messages.downloadButton())} placement="bottom">
            <span>
              <IconButton
                onClick={handleDownload}
                disabled={downloadDisabled}
                sx={{
                  color: "#fff",
                  bgcolor: "#ff8038",
                  backdropFilter: "blur(4px)",
                  "&:hover": {
                    bgcolor: "#ff6020",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "rgba(255,255,255,0.5)",
                  },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                }}
                size="small"
              >
                <DownloadIcon
                  sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t(messages.Open_in_New_Tab())} placement="bottom">
            <IconButton
              onClick={handleOpenInNewTab}
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(4px)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.2)",
                },
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
              }}
              size="small"
            >
              <OpenInNewIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(4px)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.2)",
              },
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
            }}
            size="small"
          >
            <CloseIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 0,
          overflow: "hidden",
          position: "relative",
          height: "calc(100vh - 64px)",
          width: "100%",
        }}
      >
        {displaySrc ? (
          <>
            {/* Control Buttons - Outside TransformWrapper */}
            <Box
              sx={{
                position: "absolute",
                top: { xs: 70, sm: 16 },
                right: { xs: 8, sm: 16 },
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                bgcolor: "rgba(0,0,0,0.6)",
                backdropFilter: "blur(8px)",
                borderRadius: 2,
                p: { xs: 0.5, sm: 1 },
              }}
            >
              <Tooltip title="Zoom In" placement="left">
                <IconButton
                  onClick={handleZoomIn}
                  sx={{
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.1)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                  }}
                  size="small"
                >
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom Out" placement="left">
                <IconButton
                  onClick={handleZoomOut}
                  sx={{
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.1)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                  }}
                  size="small"
                >
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Reset" placement="left">
                <IconButton
                  onClick={handleReset}
                  sx={{
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.1)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                  }}
                  size="small"
                >
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Fit to Screen" placement="left">
                <IconButton
                  onClick={handleFitScreen}
                  sx={{
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.1)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                  }}
                  size="small"
                >
                  <FitScreenIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Navigation Buttons */}
            {hasMultipleImages && (
              <>
                <IconButton
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  sx={{
                    position: "absolute",
                    left: { xs: 8, sm: 16 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    color: "#fff",
                    bgcolor: "rgba(0,0,0,0.6)",
                    backdropFilter: "blur(8px)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                    "&:disabled": {
                      bgcolor: "rgba(0,0,0,0.3)",
                      color: "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
                <IconButton
                  onClick={handleNext}
                  disabled={currentIndex === images.length - 1}
                  sx={{
                    position: "absolute",
                    right: { xs: 8, sm: 16 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    color: "#fff",
                    bgcolor: "rgba(0,0,0,0.6)",
                    backdropFilter: "blur(8px)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                    "&:disabled": {
                      bgcolor: "rgba(0,0,0,0.3)",
                      color: "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </>
            )}

            {/* Instructions Overlay */}
            <Box
              sx={{
                position: "absolute",
                top: { xs: 70, sm: 16 },
                left: { xs: 8, sm: 16 },
                zIndex: 10,
                bgcolor: "rgba(0,0,0,0.6)",
                backdropFilter: "blur(8px)",
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                maxWidth: { xs: "220px", sm: "280px" },
                display: { xs: "none", sm: "block" },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.95)",
                  display: "block",
                  mb: 0.5,
                  fontWeight: 600,
                }}
              >
                {t(messages.imagePreviewTipsLabel())}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  display: "block",
                  lineHeight: 1.5,
                  fontSize: "0.7rem",
                }}
              >
                • {t(messages.imagePreviewTipMouseWheel())}
                <br />• {t(messages.imagePreviewTipDrag())}
                <br />• {t(messages.imagePreviewTipToggle())}
                <br />• {t(messages.imagePreviewTipPinch())}
              </Typography>
            </Box>

            {/* TransformWrapper with Image */}
            <TransformWrapper
              key={displaySrc}
              ref={transformRef}
              initialScale={1}
              minScale={1}
              maxScale={6}
              centerOnInit={true}
              wheel={{ step: 0.1 }}
              pinch={{ step: 5 }}
              doubleClick={{ disabled: true }}
              panning={{ velocityDisabled: false }}
            >
              <TransformComponent
                wrapperStyle={{
                  width: "100%",
                  height: "100%",
                }}
                contentStyle={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={displaySrc}
                  alt={displayAlt}
                  onDoubleClick={handleDoubleClick}
                  onTouchEnd={handleTouchEnd}
                  style={{
                    maxWidth: "85vw",
                    maxHeight: hasMultipleImages ? "70vh" : "80vh",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
                    userSelect: "none",
                    pointerEvents: "auto",
                  }}
                />
              </TransformComponent>
            </TransformWrapper>

            {/* Thumbnail Gallery */}
            {hasMultipleImages && images.length > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  bgcolor: "rgba(0,0,0,0.8)",
                  backdropFilter: "blur(8px)",
                  borderTop: "1px solid rgba(255,255,255,0.1)",
                  p: { xs: 1, sm: 1.5 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    overflowY: "hidden",
                    pb: 0.5,
                    "&::-webkit-scrollbar": {
                      height: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      bgcolor: "rgba(255,255,255,0.1)",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "rgba(255,255,255,0.3)",
                      borderRadius: "3px",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.5)",
                      },
                    },
                  }}
                >
                  {images.map((image, index) => (
                    <Box
                      key={index}
                      ref={(el) => {
                        thumbnailRefs.current[index] =
                          el as HTMLDivElement | null;
                      }}
                      onClick={() => handleThumbnailClick(index)}
                      sx={{
                        minWidth: { xs: "60px", sm: "80px" },
                        width: { xs: "60px", sm: "80px" },
                        height: { xs: "60px", sm: "80px" },
                        borderRadius: 1,
                        overflow: "hidden",
                        cursor: "pointer",
                        border:
                          currentIndex === index
                            ? "3px solid #ff8038"
                            : "2px solid rgba(255,255,255,0.2)",
                        opacity: currentIndex === index ? 1 : 0.6,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          opacity: 1,
                          border: "2px solid rgba(255,255,255,0.5)",
                        },
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={image.srcSmall || image.src}
                        alt={image.alt || `Thumbnail ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
                {images.length > 1 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      textAlign: "center",
                      display: "block",
                      mt: 0.5,
                    }}
                  >
                    {currentIndex + 1} / {images.length}
                  </Typography>
                )}
              </Box>
            )}
          </>
        ) : (
          <Typography color="#fff">{t(messages.noImageLabel())}</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImagePreviewZoomModal;
